import { WebApp } from '@grammyjs/web-app'
import { THEME, TonConnectUI, toUserFriendlyAddress } from '@tonconnect/ui'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { client } from '@/api/orpc'
import { sleep } from '@/utils/sleep'
import { useInventory } from './inventory'
import { debounce } from '@/utils/debounce'
import { useToast } from './toast'
import { useI18n } from 'vue-i18n'
import { useLayout } from './layout'
import { useCases } from './cases'
import { useRoute, useRouter } from 'vue-router'
import { useBattles } from './battles'
import { setUser } from '@sentry/vue'
import { useRain } from './rain'

export const useUser = defineStore('user', () => {
	const { t } = useI18n({
		messages: {
			en: {
				tooOften: 'You can rotate the server seed again in {n} seconds',
				tooOftenClient: 'You can update the client seed again in {n} seconds',
				rotated: 'Old server seed is now revealed in previous games',
				updated: 'Client-seed updated',
				deposit: 'Deposited {0} coins via {1}',
				slotRefund: 'Refunded {0} coins for unfilled battle slot',
				battleStarted: 'Your battle had started!',
				sentGifts:
					'Delivery bot sent you a gift! | Delivery bot sent you {n} gifts!',
				errorSeed: 'Failed to update the client seed',
				accountBanned:
					'Your account has been banned. You can contact support in channel direct.',
				contact: 'Contact',
				noGames: 'You need to play at least one game to rotate the server seed',
				unauthorized: 'Your session has expired. Please, re-open the miniapp.',
				error: 'An error occurred. Please, try again later.',
				rainReward: 'You earned {0} coins from the Rain!'
			},
			ru: {
				tooOften:
					'Cервер-seed можно поменять через 0 секунд | Cервер-seed можно поменять через {n} секунду | Cервер-seed можно поменять через {n} секунды | Cервер-seed можно поменять через {n} секунд',
				tooOftenClient:
					'Клиент-seed можно поменять через 0 секунд | Клиент-seed можно поменять через {n} секунду | Клиент-seed можно поменять через {n} секунды | Клиент-seed можно поменять через {n} секунд',
				rotated: 'Старый сервер-seed теперь виден в предыдущих играх',
				updated: 'Клиент-seed обновлен',
				deposit: 'Пополнение на {0} коинов через {1}',
				slotRefund: 'Возврат {0} коинов за незанятый слот в батле',
				battleStarted: 'Твой батл начался!',
				sentGifts:
					'Бот доставки отправил тебе гифт! | Бот доставки отправил тебе {n} гифт! | Бот доставки отправил тебе {n} гифта! | Бот доставки отправил тебе {n} гифтов!',
				errorSeed: 'Не удалось обновить клиент-seed',
				accountBanned:
					'Ваш аккаунт был заблокирован. Вы можете связаться с поддержкой в сообщениях канала.',
				contact: 'Связаться',
				noGames: 'Чтобы сменить сервер-seed, нужно сыграть хотя бы одну игру',
				unauthorized:
					'Ваша сессия истекла. Пожалуйста, откройте мини-апп заново.',
				error: 'Произошла ошибка. Пожалуйста, попробуйте позже.',
				rainReward: 'Ты получил награду {0} коинов с Дождя!'
			}
		}
	})
	const displayCoins = ref(false)
	const coins = ref(0)
	const level = ref(0)
	const xpLevel = ref(0)
	const xp = ref(0)
	const levelPercent = ref(0)
	const uuid = ref('')
	const avatar = ref('')
	const name = ref('')
	const activatedCode = ref('')
	const activatedCodeRemovable = ref(false)
	const depositBonus = ref(0)
	const dailyCoins = ref(100)
	const referralCode = ref('')
	const telegramId = ref('')
	const nowDiff = ref(0)
	const isPremium = ref(false)
	const isSubscribed = ref(false)
	const isAdmin = ref(false)
	const isChatMember = ref(false)
	const token = ref<string>()

	const tonConnectUI = new TonConnectUI({
		manifestUrl: 'https://rage.gift/cdn/tonconnect-manifest.json',
		// buttonRootId: 'ton-connect',
		walletsRequiredFeatures: {
			sendTransaction: {
				minMessages: 1
			}
		},
		// @ts-expect-error
		twaReturnUrl: 'https://t.me/ragegiftbot',
		uiPreferences: {
			borderRadius: 's',
			theme: THEME.DARK
		}
	})

	const connectWallet = async () => {
		await tonConnectUI.openModal()
	}
	const disconnectWallet = async () => {
		await tonConnectUI.disconnect()
	}

	const tonConnected = ref(tonConnectUI.connected)
	const tonAddress = ref(tonConnectUI.wallet?.account.address)
	const tonBalance = ref(-1)
	const usdtBalance = ref(-1)

	const getWalletBalance = async () => {
		try {
			const [tonResponse, usdtResponse] = await Promise.all([
				fetch(`https://tonapi.io/v2/accounts/${tonAddress.value}`),
				fetch(
					`https://tonapi.io/v2/accounts/${tonAddress.value}/jettons/0:b113a994b5024a16719f69139328eb759596c38a25f59028b146fecdc3621dfe`
				)
			])

			const [tonData, usdtData] = await Promise.all([
				tonResponse.json(),
				usdtResponse.json()
			])

			tonBalance.value = tonData.balance
				? Number(tonData.balance) / 1_000_000_000
				: 0 // TON
			usdtBalance.value = usdtData.balance
				? Number(usdtData.balance) / 1_000_000
				: 0 // USDT
		} catch (error) {
			console.error(error)
		}
	}

	const userFriendlyAddress = computed(() => {
		if (!tonAddress.value) {
			return ''
		}

		return toUserFriendlyAddress(tonAddress.value)
	})

	const tonAddressFormatted = computed(() => {
		if (!userFriendlyAddress.value) {
			return ''
		}

		return (
			userFriendlyAddress.value.slice(0, 6) +
			'...' +
			userFriendlyAddress.value.slice(-4)
		)
	})

	tonConnectUI.onStatusChange((walletAndWalletInfo) => {
		if (!walletAndWalletInfo) {
			tonConnected.value = false
			return
		}

		tonConnected.value = true
		tonAddress.value = walletAndWalletInfo.account.address
	})

	const clientSeed = ref('devseed')

	const getSeed = async () => {
		return new Promise<{ data?: string; error?: unknown }>((res) => {
			try {
				WebApp.CloudStorage.getItem('clientSeed', (err, data) => {
					if (err || !data) {
						return res({})
					}

					res({ data })
				})
			} catch (error: unknown) {
				console.error('error getting seed', error)
				res({ error })
			}
		})
	}

	const randomSeed = () => {
		return Math.random().toString(36).substring(2, 12)
	}

	const saveSeed = async (seed: string) => {
		return new Promise<{ data?: string; error?: unknown }>((res) => {
			try {
				WebApp.CloudStorage.setItem('clientSeed', seed, (err) => {
					if (err) {
						return res({})
					}

					clientSeed.value = seed
					res({ data: seed })
				})
			} catch (error: unknown) {
				console.error('error saving seed', seed, error)
				res({ error })
			}
		})
	}

	const auth = async () => {
		const { error, isDefined, data } = await client.user.auth({
			initData:
				import.meta.env.VITE_INIT_DATA || decodeURIComponent(WebApp.initData)
		})

		if (isDefined) {
			if (error.code === 'UNAUTHORIZED') {
				toast.add({
					type: 'error',
					text: t('unauthorized')
				})
			}
			if (error.code === 'ACCOUNT_BANNED') {
				toast.add({
					type: 'info',
					text: t('accountBanned'),
					action: {
						text: t('contact'),
						onClick: () => WebApp.openLink('https://t.me/ragegift')
					},
					duration: 30000
				})
			}

			return false
		}

		if (error) {
			console.error(error)
			toast.add({
				type: 'error',
				text: t('error')
			})
			return false
		}

		if (!data) {
			return false
		}

		token.value = data.token

		return data.signUp || false
	}

	const inventory = useInventory()
	const cases = useCases()
	const battles = useBattles()
	const rain = useRain()

	const getMe = async () => {
		const [{ error, data, isDefined }, { data: seed }] = await Promise.all([
			client.user.getMe(),
			getSeed()
		])

		if (isDefined) {
			if (error.code === 'UNAUTHORIZED') {
				return
			}
			if (error.code === 'ACCOUNT_BANNED') {
				toast.add({
					type: 'info',
					text: t('accountBanned'),
					action: {
						text: t('contact'),
						onClick: () => WebApp.openLink('https://t.me/ragegift')
					},
					duration: 30000
				})
				return
			}
		}

		if (error) {
			console.error(error)
			return
		}

		if (!data) {
			return
		}

		coins.value = data.coins
		uuid.value = data.uuid
		avatar.value = data.avatar || ''
		name.value = data.name

		rain.setRain(
			data.rain
				? {
						status: data.rain.status,
						endsAt: data.rain.endsAt || undefined,
						pool: data.rain.pool,
						joined: data.rain.joined
					}
				: undefined
		)

		setUser({ id: data.uuid, username: data.name })

		referralCode.value = data.affiliate.referralCode
		telegramId.value = data.telegramId

		maxStarsDeposit.value = data.maxStarsDeposit

		setAffiliate(
			data.affiliate.affiliatedCode || '',
			data.affiliate.depositBonusPercent,
			data.affiliate.affiliatedCodeEditable,
			data.affiliate.dailyDepositCoins
		)

		cases.setWelcomeCase(
			data.affiliate.welcomeCase.uuid,
			data.affiliate.welcomeCase.count,
			'Welcome Case'
		)

		cases.setReferralCase(
			data.affiliate.referralCase.uuid,
			data.affiliate.referralCase.canOpen,
			data.affiliate.referralCase.requiredReferrals,
			data.affiliate.referralCase.qualifiedReferrals
		)

		level.value = data.rewards.level
		levelPercent.value = data.rewards.levelPercent
		xp.value = data.rewards.xp
		xpLevel.value = data.rewards.levelExpSize
		isPremium.value = WebApp.initDataUnsafe?.user?.is_premium || false

		setChatMember(data.isChatMember)

		if (!seed) {
			saveSeed(randomSeed())
		} else {
			clientSeed.value = seed
		}

		cases.setRewardCasesCount(data.rewards.casesCount)

		if (data.isAdmin) {
			isAdmin.value = data.isAdmin
		}

		console.info(
			`%c👀 Hi ${data.username}! What brings you here? %c`,
			'background: #ff4757; color: white; padding: 8px 12px; border-radius: 8px; font-weight: bold; font-size: 16px; text-shadow: 1px 1px 2px rgba(0,0,0,0.5);',
			'color: #ffa502; font-weight: bold; font-size: 14px;'
		)

		inventory.getItems()
		getProfile()
		subToUpdates()
		battles.subToUpdates()
		rain.subToRain()
	}

	const nowCallbacks: (() => void)[] = []

	const getTimeOnce = async () => {
		const startAt = Date.now()

		const { error, data } = await client.user.getTime()

		if (error) {
			console.error(error)
			return Infinity
		}

		if (!data) {
			return Infinity
		}

		const diff = (Date.now() - startAt) / 2

		return Date.now() - (data.ts + diff)
	}

	const getTime = async () => {
		try {
			const promises = []

			for (let i = 0; i < 5; i++) {
				promises.push(getTimeOnce())
			}

			const results = await Promise.all(promises)

			nowDiff.value = results.reduce((a, b) => Math.min(a, b), Infinity)

			nowCallbacks.forEach((callback) => callback())
		} catch (error) {
			console.error(error)
		}
	}

	const now = () => {
		return Date.now() - nowDiff.value
	}

	const nowAvailable = async () => {
		if (nowDiff.value) {
			return
		}

		return new Promise<void>((resolve) => {
			nowCallbacks.push(resolve)
		})
	}

	getTime()

	const setBalance = (balance: number) => {
		coins.value = balance
	}

	const setActivatedCode = (code: string) => {
		activatedCode.value = code
	}

	const setAffiliate = (
		code: string,
		bonus: number,
		removable: boolean,
		daily: number
	) => {
		setActivatedCode(code)
		depositBonus.value = bonus
		activatedCodeRemovable.value = removable
		dailyCoins.value = daily
	}

	const layout = useLayout()

	const activating = ref(false)

	const activateCode = async (affiliatedCode: string) => {
		activating.value = true

		const { error, data, isDefined } = await client.user.updateAffiliatedCode({
			affiliatedCode
		})

		activating.value = false

		if (isDefined) {
			if (error.code === 'AFFILIATED_CODE_NOT_FOUND') {
				return false
			}
		}

		if (!data) {
			return false
		}

		setAffiliate(
			data.updatedUser.affiliate.affiliatedCode || '',
			data.updatedUser.affiliate.depositBonusPercent,
			data.updatedUser.affiliate.affiliatedCodeEditable,
			data.updatedUser.affiliate.dailyDepositCoins
		)

		cases.setWelcomeCase(
			data.updatedUser.affiliate.welcomeCase.uuid,
			data.updatedUser.affiliate.welcomeCase.count,
			'Welcome Case'
		)

		cases.setReferralCase(
			data.updatedUser.affiliate.referralCase.uuid,
			data.updatedUser.affiliate.referralCase.canOpen,
			data.updatedUser.affiliate.referralCase.requiredReferrals,
			data.updatedUser.affiliate.referralCase.qualifiedReferrals
		)

		return true
	}

	const setUpdated = (
		balance: number,
		levelNew: number,
		xpNew: number,
		xpLevelNew: number,
		levelPercentNew: number
	) => {
		coins.value = balance
		xp.value = xpNew
		xpLevel.value = xpLevelNew
		levelPercent.value = levelPercentNew
		level.value = levelNew
	}

	const addBalance = (amount: number) => {
		coins.value += amount
	}

	const unsettledTransactions = ref<
		{ uuid: string; coins: number; settledAt: number }[]
	>([])

	const pushUnsettledTransactions = (
		transactions: { uuid: string; coins: number; settledAt: number }[]
	) => {
		unsettledTransactions.value.push(...transactions)

		for (const t of transactions) {
			settleTransaction(t.uuid, t.settledAt)
		}
	}

	const settleTransaction = async (uuid: string, settledAt: number) => {
		if (settledAt === -1) {
			return
		}

		await sleep(settledAt - now())

		const tx = unsettledTransactions.value.find((t) => t.uuid === uuid)

		if (!tx) {
			return
		}

		if (tx.settledAt !== settledAt) {
			return
		}

		unsettledTransactions.value = unsettledTransactions.value.filter(
			(t) => t.uuid !== uuid
		)
	}

	const resettleTransaction = (uuid: string, settledAt: number) => {
		const tx = unsettledTransactions.value.find((t) => t.uuid === uuid)

		if (!tx) {
			return false
		}

		tx.settledAt = settledAt

		settleTransaction(uuid, settledAt)

		return true
	}

	const displayedCoins = computed(() =>
		unsettledTransactions.value.length
			? coins.value -
				unsettledTransactions.value.reduce((a, b) => a + b.coins, 0)
			: coins.value
	)

	const setDisplayCoins = (value: boolean) => {
		displayCoins.value = value
	}

	const profile = ref(false)
	const bestUnboxing = ref(false)
	const modelName = ref('')
	const collectionName = ref('')
	const modelUuid = ref('')
	const preloader = ref<string>()
	const price = ref(0)
	const caseUuid = ref('')
	const caseName = ref('')
	const loadingProfile = ref(true)
	const battlesPlayed = ref(0)
	const casesOpened = ref(0)
	const seedPairGames = ref(0)
	const serverSeedHash = ref('')

	const getProfile = async () => {
		loadingProfile.value = true

		const { data, error } = await client.user.getProfile()

		loadingProfile.value = false

		profile.value = true

		if (error) {
			return
		}
		if (!data) {
			return
		}

		if (data.bestUnboxing) {
			bestUnboxing.value = true
			modelName.value = data.bestUnboxing.modelName
			collectionName.value = data.bestUnboxing.collectionName
			modelUuid.value = data.bestUnboxing.modelUuid
			preloader.value = data.bestUnboxing.preloader || undefined
			price.value = data.bestUnboxing.price
			caseUuid.value = data.bestUnboxing.caseUuid
			caseName.value = data.bestUnboxing.caseName
		}

		battlesPlayed.value = data.battlesPlayed
		casesOpened.value = data.casesOpened

		seedPairGames.value = data.fairness.serverSeedGames
		serverSeedHash.value = data.fairness.serverSeedHash
	}

	const loadingTransactions = ref(false)
	const pages = ref(0)
	const page = ref(1)
	const transactions = ref<
		{
			coins?: number
			createdAt: number
			type:
				| 'deposit_ton'
				| 'deposit_ton_tether'
				| 'deposit_stars'
				| 'deposit_cryptobot'
				| 'withdraw_ton'
				| 'case_open'
				| 'item_sell'
				| 'battle_create'
				| 'battle_join'
				| 'battle_reward'
				| 'referral_claim'
				| 'market_buy'
				| 'item_claim'
				| 'reward_case_open'
				| 'battle_slot_funding_refund'
				| 'referral_case_open'
				| 'welcome_case_open'
				| 'refill_admin'
				| 'rain_reward'
				| 'rain_tip'
			case?: {
				uuid: string
				name: string
			}
			battleInfo?: string
			item?: {
				modelUuid: string
				modelName: string
				collectionName: string
				price?: number
				chance?: number
			}
			fair?: {
				nonce: number
				clientSeed: string
				serverSeed: string | null
				serverSeedHash: string
				ticketsCount: number
				ticket: number
			}
		}[]
	>([])

	const activeTransactionFilter = ref(0)

	const getTransactions = async () => {
		let filter: 'all' | 'games' | 'deposits' | 'purchases' | 'claims' = 'all'

		if (activeTransactionFilter.value === 1) {
			filter = 'games'
		} else if (activeTransactionFilter.value === 2) {
			filter = 'deposits'
		} else if (activeTransactionFilter.value === 3) {
			filter = 'purchases'
		} else if (activeTransactionFilter.value === 4) {
			filter = 'claims'
		}

		const { error, data } = await client.user.getProfileTransactions({
			page: page.value,
			filter
		})

		if (error) {
			console.error(error)
			return undefined
		}

		return data
	}

	const filterChangedTransactions = async () => {
		if (loadingTransactions.value) {
			return
		}

		loadingTransactions.value = true

		page.value = 1

		const data = await getTransactions()

		if (data) {
			transactions.value = data.transactions
			pages.value = data.pages
		}

		loadingTransactions.value = false
	}

	watch(activeTransactionFilter, () => filterChangedTransactions())

	let fetchingMore = false

	const fetchMore = async () => {
		if (fetchingMore) {
			return
		}
		if (page.value >= pages.value) {
			return
		}

		fetchingMore = true

		page.value++
		const data = await getTransactions()

		if (data) {
			transactions.value.push(...data.transactions)
			pages.value = data.pages
		}

		fetchingMore = false
	}

	const debouncedFetchMoreTransactions = debounce(fetchMore, 500)

	const toast = useToast()

	const rotateServerSeed = async () => {
		const { error, data, isDefined } = await client.user.rotateServerSeed()

		if (isDefined) {
			if (error.code === 'TOO_OFTEN') {
				toast.add({
					type: 'error',
					text: t('tooOften', error.data.retryAfter)
				})
				return
			}
			if (error.code === 'NO_GAMES_PLAYED') {
				toast.add({
					type: 'error',
					text: t('noGames')
				})
				return
			}

			return console.error(error)
		}

		if (error) {
			return console.error(error)
		}

		if (!data) {
			return
		}

		serverSeedHash.value = data.serverSeedHash
		seedPairGames.value = 0

		toast.add({
			type: 'success',
			text: t('rotated')
		})
	}

	const updateClientSeed = async (seed: string) => {
		const { data } = await saveSeed(seed)

		if (!data) {
			toast.add({
				type: 'error',
				text: t('errorSeed')
			})
			return
		}

		toast.add({
			type: 'success',
			text: t('updated')
		})
	}

	const router = useRouter()
	const route = useRoute()

	const subToUpdates = async () => {
		const { data: iterator, error } = await client.user.subUserUpdates(
			{},
			{
				context: {
					retry: Number.POSITIVE_INFINITY
				}
			}
		)

		if (error) {
			return console.error(error)
		}

		if (!iterator) {
			return
		}

		try {
			for await (const event of iterator) {
				if (event.type === 'balance_updated') {
					setBalance(event.updatedUser.coins)

					if (event.source === 'battle_slot_funding_refund') {
						toast.add({
							type: 'success',
							text: t('slotRefund', [event.coins / 100])
						})
						continue
					}
					if (event.source === 'rain_reward') {
						toast.add({
							type: 'success',
							text: t('rainReward', [event.coins / 100])
						})
						continue
					}

					toast.add({
						type: 'success',
						text: t('deposit', [
							event.coins / 100,
							event.source
								.replace('deposit_', '')
								.split('_')
								.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
								.join(' ')
						])
					})

					layout.hideDepositModal()

					if (route.path === '/onboarding') {
						router.replace('/')
					}
				}
				if (event.type === 'inventory_updated') {
					const sent = event.inventoryItems.filter(
						(item) => !item.errorCode
					).length

					if (sent) {
						toast.add({
							type: 'success',
							text: t('sentGifts', sent)
						})
					}

					inventory.updateItems(event.inventoryItems)
				}
			}
		} catch (error: unknown) {
			console.error('ERROR IN USER SUB', error)
		}
	}

	const setPremiumAndSubscription = (premium: boolean, subscribed: boolean) => {
		isPremium.value = premium
		isSubscribed.value = subscribed
	}

	const battleStarted = (uuid: string) => {
		toast.add({
			duration: 5000,
			text: t('battleStarted'),
			type: 'info',
			action: {
				text: 'View',
				onClick: () => {
					router.push(`/battle/${uuid}`)
				}
			}
		})
	}

	const starsEnabled = ref(true)
	const maxStarsDeposit = ref(0)

	const disableStars = () => {
		starsEnabled.value = false
	}

	const setChatMember = (value: boolean) => {
		isChatMember.value = value
	}

	return {
		auth,
		token,
		getMe,

		displayCoins,
		displayedCoins,
		coins,
		uuid,
		xp,
		xpLevel,
		level,
		levelPercent,
		name,
		avatar,
		referralCode,
		telegramId,
		activatedCode,
		activatedCodeRemovable,
		setActivatedCode,
		dailyCoins,
		depositBonus,
		addBalance,
		setBalance,
		setUpdated,
		setAffiliate,
		activateCode,

		ton: tonConnectUI,
		tonConnected,
		tonAddress,
		tonBalance,
		usdtBalance,
		tonAddressFormatted,
		userFriendlyAddress,
		connectWallet,
		disconnectWallet,
		getWalletBalance,

		now,
		nowAvailable,
		pushUnsettledTransactions,
		resettleTransaction,
		settleTransaction,
		setDisplayCoins,

		profile,
		bestUnboxing,
		modelName,
		collectionName,
		modelUuid,
		preloader,
		price,
		caseUuid,
		caseName,
		loadingProfile,
		battlesPlayed,
		casesOpened,
		seedPairGames,
		clientSeed,
		serverSeedHash,
		getProfile,
		rotateServerSeed,
		updateClientSeed,
		randomSeed,
		saveSeed,

		transactions,
		page,
		pages,
		loadingTransactions,
		activeTransactionFilter,
		debouncedFetchMoreTransactions,
		filterChangedTransactions,

		setPremiumAndSubscription,
		isPremium,
		isSubscribed,
		isChatMember,
		setChatMember,
		activating,

		battleStarted,

		isAdmin,

		starsEnabled,
		disableStars,
		maxStarsDeposit,

		rain
	}
})
